import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Stage, Layer, Rect, Group, Text } from 'react-konva';

export default function KonvaBoxes({
  boxes,                 // steps per box array
  stepsPerBox,           // total steps per box
  unitLabel,
  measureIncrement,      // step size in base units (for label only)
  measureMaxVal,         // per-box capacity
  actionable,            // can interact?
  onInc, onDec,          // handlers (dec not used here but available)
}) {
  const wrapRef = useRef(null);
  const [size, setSize] = useState({ w: 720, h: 240 });

  // Layout constants
  const PADDING = 8;
  const GAP = 16;
  const BOX_W = 160;
  const BOX_H = 92;

  // Responsive width via ResizeObserver
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      const cr = entries[0].contentRect;
      const width = Math.max(320, cr.width);
      const cols = Math.max(1, Math.floor((width - PADDING * 2 + GAP) / (BOX_W + GAP)));
      const rows = Math.ceil((boxes?.length || 0) / cols) || 1;
      const height = rows * (BOX_H + GAP) - GAP + PADDING * 2;
      setSize({ w: width, h: height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [boxes?.length]);

  const cols = useMemo(() => {
    return Math.max(1, Math.floor((size.w - PADDING * 2 + GAP) / (BOX_W + GAP)));
  }, [size.w]);

  const getXY = (idx) => {
    const row = Math.floor(idx / cols);
    const col = idx % cols;
    const x = PADDING + col * (BOX_W + GAP);
    const y = PADDING + row * (BOX_H + GAP);
    return { x, y };
  };

  const formatAmount = (steps) => {
    const inc = measureIncrement > 0 ? measureIncrement : 1;
    const val = steps * inc;
    return `${val.toFixed(0)}${unitLabel}`;
  };

  const handleEnter = (e) => {
    if (!actionable) return;
    e.target.getStage().container().style.cursor = 'pointer';
  };
  const handleLeave = (e) => {
    e.target.getStage().container().style.cursor = 'default';
  };

  return (
    <div ref={wrapRef} style={{ width: '100%' }}>
      <Stage width={size.w} height={size.h}>
        <Layer>
          {(boxes || []).map((steps, idx) => {
            const { x, y } = getXY(idx);
            const pct = stepsPerBox ? steps / stepsPerBox : 0;
            const fillW = Math.max(0, Math.min(BOX_W, Math.round(BOX_W * pct)));

            return (
              <Group
                key={idx}
                x={x}
                y={y}
                listening={actionable}
                onClick={actionable ? () => onInc(idx) : undefined}
                onTap={actionable ? () => onInc(idx) : undefined}
                onMouseEnter={handleEnter}
                onMouseLeave={handleLeave}
              >
                <Rect
                  width={BOX_W}
                  height={BOX_H}
                  stroke="#444"
                  strokeWidth={2}
                  cornerRadius={6}
                  fill="#f8f9fa"
                />
                <Rect
                  width={fillW}
                  height={BOX_H}
                  cornerRadius={[6, 0, 0, 6]}
                  fillLinearGradientStartPoint={{ x: 0, y: 0 }}
                  fillLinearGradientEndPoint={{ x: 0, y: BOX_H }}
                  fillLinearGradientColorStops={[0, '#007bff', 1, '#0056b3']}
                />
                <Text
                  text={`${formatAmount(steps)} / ${measureMaxVal}${unitLabel}`}
                  x={0}
                  y={BOX_H / 2 - 9}
                  width={BOX_W}
                  align="center"
                  fontSize={14}
                  fill="#0d6efd"
                  listening={false}
                />
              </Group>
            );
          })}
        </Layer>
      </Stage>
    </div>
  );
}